import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { FolderInput, MessageCircle, Share, Trash2 } from 'lucide-react';
import type { LibraryCard } from '@/lib/library/libraryCard';
import { fanSpots, hideAt } from '@/lib/library/cornerFan';
import { cn } from '@/lib/utils';
import { useFanSpring } from './useFanSpring';

export type TileMenuAction = 'share' | 'save' | 'comment' | 'delete';

export interface TileMenuTarget {
  card: LibraryCard;
  /** The tile's picture on screen when the press landed. */
  rect: DOMRect;
  /** The tile's corner radius at this density: the fan rides the same corner. */
  radius: number;
  /** Opened by a long press with the finger still down: sliding onto a button and lifting picks it. */
  byTouch: boolean;
}

/** The button; everything with a size is measured off it. */
const DOT = 40;
const R = DOT / 2;
/** Air between the card and a button's edge. */
const GAP = 12;
/** Centre to centre along the path. */
const SPACING = DOT + 18;
/** The beat between one button and the next opening. */
const BEAT = 70;
/** The spring's liveliness, 0..100 (see springOf). */
const BOUNCE = 22;
/** Labels are wider than SPACING: neighbours on a top or bottom arm alternate rows. */
const STAGGER = 30;
/** A finger within this distance of a button's centre is over it. */
const HIT = R + 14;

const ACTS: { id: TileMenuAction; label: string; Icon: typeof Share; danger?: boolean }[] = [
  { id: 'share', label: 'Share', Icon: Share },
  { id: 'save', label: 'Save to space', Icon: FolderInput },
  { id: 'comment', label: 'Comment', Icon: MessageCircle },
  // Last out of the corner, so it is the hardest one to land on by accident; it still asks first
  { id: 'delete', label: 'Delete', Icon: Trash2, danger: true },
];
const N = ACTS.length;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * What a long press on a tile opens, after the ActionNode corner fan: the
 * page dims, the tile lifts, and its actions come OUT of its corner one
 * after another on their own springs, riding a path offset from the card's
 * own outline, so the corner is visibly where they came from. Closing folds
 * them back last-out-first-in.
 *
 * Tiles on the right fan from their left corner and tiles near the top from
 * their bottom corner, so the fan always opens onto the screen.
 */
export function LibraryTileMenu({
  target,
  onAction,
  onClose,
}: {
  target: TileMenuTarget;
  /** Called inside the gesture that picked it, so Share keeps its user activation. */
  onAction: (action: TileMenuAction, card: LibraryCard) => void;
  onClose: () => void;
}) {
  const still = useReducedMotion() ?? false;
  const [on, setOn] = useState(false);
  const [lit, setLit] = useState<boolean[]>(() => ACTS.map(() => false));
  const [hover, setHover] = useState<number | null>(null);
  const closingRef = useRef(false);
  const timers = useRef<number[]>([]);

  // Open on the next frame, so every spring starts from inside the corner
  useEffect(() => {
    const f = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(f);
  }, []);

  // Each button aims a beat after the one before it; closing runs backwards
  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    const gap = still ? 0 : BEAT;
    for (let i = 0; i < N; i += 1) {
      const at = gap * (on ? i : N - 1 - i);
      const set = () =>
        setLit((prev) => {
          if (prev[i] === on) return prev;
          const next = [...prev];
          next[i] = on;
          return next;
        });
      if (at <= 0) set();
      else timers.current.push(window.setTimeout(set, at));
    }
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [on, still]);

  // Hooks cannot be conditional: one spring per action, always
  const p0 = useFanSpring(lit[0] ? 100 : 0, BOUNCE, still);
  const p1 = useFanSpring(lit[1] ? 100 : 0, BOUNCE, still);
  const p2 = useFanSpring(lit[2] ? 100 : 0, BOUNCE, still);
  const p3 = useFanSpring(lit[3] ? 100 : 0, BOUNCE, still);
  const progress = [p0, p1, p2, p3];

  // Geometry, in screen pixels
  const { rect } = target;
  const corner = clamp(target.radius, 0, 32);
  const fanRight = rect.left + rect.width / 2 < window.innerWidth / 2;
  const flip = rect.top < 128;
  const sx = fanRight ? 1 : -1;
  const sy = flip ? -1 : 1;
  const cx = fanRight ? rect.right : rect.left;
  const cy = flip ? rect.bottom : rect.top;
  const spots = fanSpots(N, corner, GAP, R, SPACING).map((s) => ({ x: cx + sx * s.x, y: cy + sy * s.y, nx: sx * s.nx, ny: sy * s.ny }));
  // How far out each label sits: on the side arm labels stack, on a top or bottom arm every second one drops a row
  let flat = 0;
  const labelOff = spots.map((s) => (Math.abs(s.ny) > 0.7 && flat++ % 2 === 1 ? R + 8 + STAGGER : R + 8));
  const deep = hideAt(corner, R) / Math.SQRT2;
  const hid = { x: cx - sx * deep, y: cy + sy * deep };

  const close = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setHover(null);
    setOn(false);
    window.setTimeout(onClose, still ? 0 : BEAT * (N - 1) + 260);
  }, [onClose, still]);

  const choose = useCallback(
    (i: number) => {
      if (closingRef.current) return;
      onAction(ACTS[i].id, target.card);
      close();
    },
    [close, onAction, target.card],
  );

  // Press, slide, release: while the finger that opened the menu is down, the
  // button under it is highlighted and lifting over one picks it
  const spotsRef = useRef(spots);
  useEffect(() => {
    spotsRef.current = spots;
  });
  useEffect(() => {
    if (!target.byTouch) return;
    let tracking = true;
    const nearest = (x: number, y: number) => {
      let best: number | null = null;
      let bestD = HIT;
      spotsRef.current.forEach((s, i) => {
        const d = Math.hypot(s.x - x, s.y - y);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      return best;
    };
    const onMove = (e: TouchEvent) => {
      if (!tracking || !e.touches[0]) return;
      setHover(nearest(e.touches[0].clientX, e.touches[0].clientY));
    };
    const onEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const i = t ? nearest(t.clientX, t.clientY) : null;
      setHover(null);
      if (i !== null) choose(i);
    };
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [choose, target.byTouch]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [close]);

  const card = target.card;

  return createPortal(
    // No text selection or iOS callout anywhere in the menu: the finger that opened it is still down
    <div
      // touch-none: the page scrolls now, and must stay put under the open menu
      className="fixed inset-0 z-50 touch-none select-none [-webkit-touch-callout:none] [-webkit-tap-highlight-color:transparent]"
      role="dialog"
      aria-modal="true"
      aria-label="Item actions"
      onContextMenu={(e) => e.preventDefault()}
    >
      <motion.div
        className="absolute inset-0 bg-paper"
        initial={{ opacity: 0 }}
        animate={{ opacity: on ? 0.86 : 0 }}
        transition={{ duration: still ? 0 : 0.18, ease: [0.22, 1, 0.36, 1] }}
        onClick={close}
      />

      {/* The tile, lifted off the page */}
      <motion.div
        aria-hidden
        className={cn('pointer-events-none fixed overflow-hidden border border-line', card.thumbnail ? 'bg-paper-sunken' : 'bg-paper-raised')}
        style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, borderRadius: target.radius }}
        initial={{ scale: 1, boxShadow: '0 0 0 rgba(0,0,0,0)' }}
        animate={on && !still ? { scale: 1.035, boxShadow: '0 1px 0 rgba(23,24,26,0.04), 0 16px 36px -12px rgba(23,24,26,0.28)' } : { scale: 1, boxShadow: '0 0 0 rgba(0,0,0,0)' }}
        transition={{ type: 'spring', stiffness: 500, damping: 38 }}
      >
        {card.thumbnail ? (
          <img src={card.thumbnail} alt="" draggable={false} className="h-full w-full object-cover" />
        ) : (
          <p className="whitespace-pre-line [overflow-wrap:anywhere] p-3 text-[13px] leading-[19px] text-ink-2">{card.text || card.title}</p>
        )}
      </motion.div>

      {ACTS.map((a, i) => {
        const u = clamp(progress[i] / 100, 0, 1.4);
        const spot = spots[i];
        const put = (from: number, to: number) => from + (to - from) * u;
        const x = put(hid.x, spot.x);
        const y = put(hid.y, spot.y);
        const active = hover === i;
        const tone = active ? (a.danger ? 'border-destructive bg-destructive text-destructive-foreground' : 'border-ink bg-ink text-paper') : null;
        const shown = Math.min(1, u);
        return (
          <React.Fragment key={a.id}>
            <button
              type="button"
              aria-label={a.label}
              tabIndex={on ? 0 : -1}
              onClick={() => choose(i)}
              className={cn(
                'fixed flex items-center justify-center rounded-full border shadow-md outline-none transition-colors duration-100 focus-visible:ring-2 focus-visible:ring-signal',
                tone ?? 'border-line bg-paper-raised text-ink',
              )}
              style={{
                width: DOT,
                height: DOT,
                left: x - R,
                top: y - R,
                // Small while still mostly inside the corner, full size as it arrives; a little more under the finger
                transform: `scale(${(0.82 + 0.18 * shown) * (active ? 1.1 : 1)})`,
              }}
            >
              <a.Icon className="h-[18px] w-[18px]" strokeWidth={1.9} aria-hidden />
            </button>
            {/* The label sits outward from the card, along the direction the button came out */}
            <button
              type="button"
              tabIndex={-1}
              aria-hidden
              onClick={() => choose(i)}
              className={cn(
                'fixed whitespace-nowrap rounded-full border px-2.5 py-1 text-[12px] font-medium leading-[16px] outline-none transition-colors duration-100',
                tone ?? 'border-line bg-paper-raised text-ink-2',
              )}
              style={{
                left: spot.x + spot.nx * labelOff[i],
                top: spot.y + spot.ny * labelOff[i],
                transform: `translate(${-50 + 50 * spot.nx}%, ${-50 + 50 * spot.ny}%)`,
                opacity: clamp((u - 0.55) / 0.45, 0, 1),
              }}
            >
              {a.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>,
    document.body,
  );
}
